import Button from "components/Button/Button";
import { LegacyRef, useState } from "react";
import "./InputField.scss";

interface IProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: string;
  numeric?: boolean;
  floating?: boolean;
  innerRef?: LegacyRef<HTMLInputElement>;
  showHideButton?: boolean;
  errors?: string[];
  commaList?: boolean;
}

const InputField = ({
  label,
  icon,
  numeric,
  floating,
  innerRef,
  showHideButton, // icon and hide button share the same place so don"t use them together on one input.
  errors,
  commaList,
  ...props
}: IProps) => {
  const [displayType, setDisplayType] = useState<React.HTMLInputTypeAttribute>(
    props.type || "text"
  );

  const isModifierKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const macOsPattern = /Mac|iPod|iPhone|iPad/;
    const isMac = macOsPattern.test(window.navigator.userAgent)
    return event.ctrlKey || (isMac && event.metaKey);
  }

  const handleNumericOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "ArrowLeft" || 
      event.key === "ArrowRight" || 
      event.key === "Delete" || 
      event.key === "Backspace" || 
      event.key === "Tab"
    ) {
      return;
    }
    if (numeric && commaList && event.key === ",") {
      return;
    }

    // if ctrl+v / cmd+v is pressed we let handlePaste do the validation
    if (isModifierKey(event) && event.key === "v") {
      return;
    }

    // handle ctrl+c / cmd+c
    if (isModifierKey(event) && event.key === "c") {
      return;
    }

    // handle ctrl+a / cmd+a
    if (isModifierKey(event) && event.key === "a") {
      return;
    }

    // we want to handle floating numbers
    if (event.key === "." && (event.target as any).value.includes(".")) {
      event.preventDefault();
      return;
    }
    if (floating && !/\d/.test(event.key) && event.key !== ".") {
      event.preventDefault();
    }
    if (!floating && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  };


  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (numeric) {
      const clipboard = event.clipboardData.getData("text");
      if (!/\d/.test(clipboard)) {
        event.preventDefault();
        return;
      }
    }
    event.persist();
  };

  const displayPasswordButton = () => {
    if (showHideButton && props.type === "password") {
      return (
        <Button
          type="button"
          className="button--secondary button--solid"
          icon={displayType === "password" ? "eye" : "eye-blocked"}
          title={`${displayType === "password" ? "Show" : "Hide"} Password`}
          onClick={() => {
            if (displayType === "password") {
              setDisplayType("text");
            } else {
              setDisplayType("password");
            }
          }}
          tabIndex={-1}
        ></Button>
      );
    }
    if (showHideButton && props.type === "locale") {
      return (
        <>
          <Button
            type="button"
            className="button--secondary button--solid"
            icon={"message-typing"}
            title={"Locale (format: en-EN, de-DE)"}
            onClick={() => {}}
            tabIndex={-1}
          ></Button>
        </>
      );
    }
  };

  return (
    <div className={"input-field " + props.className}>
      {label && <label className="input-field__label">{label}</label>}
      <div className="inputWithButton">
        <input
          {...props}
          ref={innerRef}
          type={displayType}
          className="input-field__input"
          onPaste={handlePaste}
          onKeyDown={
            numeric
              ? handleNumericOnly
              : (e) => {
                  e.persist();
                }
          }
        />
        {displayPasswordButton()}
        {icon ? <span className={"input-field__icon icon-" + icon}></span> : ""}
      </div>
      {errors?.length! > 0 && (
        <p>
          {errors?.map((e, i) => (
            <small key={`validation-error-${i}`}>{e}</small>
          ))}
        </p>
      )}
    </div>
  );
};

export default InputField;
